import React, { useState, useEffect } from "react";
import { Modal, Row, Col, Card, Form } from "react-bootstrap";
import { instance } from "../../../utils/utils";

const plans = [
    {
        id: "basic",
        name: "Elevva BASIC",
        size: "lg",
        description:
            "Pensada para gimnasios pequeños, estudios boutique o entrenadores que quieren comenzar digitalizando sus operaciones.",
        price: "$2.000",
        priceNote: "COP / usuario / mes",
        subtitle: "Plan mensual con 50 usuarios",
        featuresTitle: "Características incluidas",
        features: [
            "App de gestión para clientes.",
            "Onboarding digital de nuevos clientes (datos personales, objetivos y lesiones).",
            "Registro de datos personales, actividad física, objetivos y lesiones.",
            "Aceptación digital del PAR-Q.",
            "Firma digital de contratos de exclusión de responsabilidad y uso de imagen.",
            "Gestión de base de datos de usuarios.",
            "Módulo de reconocimiento facial para el ingreso de clientes.",
            "Hasta 4 usuarios administrativos.",
            "Hasta 3 perfiles de usuarios administrativos.",
            "Creación y gestión de planes de entrenamiento individuales y grupales.",
            "Gestión de reservas para clases.",
            "Administración de planes de usuarios.",
            "Panel de gráficas de salud y desempeño del negocio.",
            "Descarga de movimientos financieros (conciliación bancaria).",
            "Módulo de valoración y control de usuarios.",
            "Definición de objetivos básicos del usuario.",
            "Creación de retos y registros de RM.",
            "Gráficas de evolución física de los usuarios.",
            "Módulo de comunicación unidireccional (buzón de sugerencias cliente → gimnasio)."
        ],
        recommended: "Ideal para centros que quieren profesionalizar su operación con bajo costo de entrada.",
    },
    {
        id: "pro",
        name: "Elevva PLUS",
        size: "md",
        description:
            "Diseñada para gimnasios en crecimiento que buscan más interacción con sus clientes y gestión avanzada de negocio.",
        price: "$3.000",
        priceNote: "COP / usuario / mes",
        subtitle: "Plan mensual con 50 usuarios.",
        featuresTitle: "Todo incluido en Basic, más",
        features: [
            "Módulo de Negocios Avanzado: Contabilidad automatizada para tu tienda y servicios extra (aumenta tu ticket promedio).",
            "Experiencia 'Group Training' Pro: Conecta hasta 20 usuarios a la pantalla grande.",
            "Bio-Feedback en Tiempo Real: Muestra el rendimiento cardíaco de tus clientes para clases más adictivas y seguras.",
        ],
        highlighted: true,
        recommended: "Ideal para gimnasios medianos",
    },
    {
        id: "enterprise",
        name: "Elevva PRIME",
        size: "md",
        description:
            "IA avanzada y monitoreo biométrico para centros de alto rendimiento.",
        price: "$4.000",
        priceNote: "COP / usuario / mes",
        subtitle: "Plan mensual con 50 usuarios.",
        featuresTitle: "Lo que incluye:",
        features: [
            "Módulo de clases virtuales (grupales e individuales) con visualización de signos vitales en tiempo real.",
            "Inteligencia artificial para creación de planes individuales.",
            "Inteligencia artificial para análisis financiero del negocio.",
            "Descuentos en tienda interna de hasta el 30%.",
            "Soporte VIP y consultoría de implementación.",
            "Actualizaciones prioritarias y acceso anticipado a nuevas funciones.",
        ],
        recommended: "Ideal para negocios que quieren diferenciarse con tecnología avanzada y ofrecer una experiencia de nivel internacional.",
        disabled: true,
    },
];

const PlansModal = ({ show, onHide, onPlanSelected, onBack, source, initialBillingPeriod }) => {
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [billingPeriod, setBillingPeriod] = useState(initialBillingPeriod || "yearly");
    const [expandedPlans, setExpandedPlans] = useState({});
    const [backendPlans, setBackendPlans] = useState([]);
    const [planConfigs, setPlanConfigs] = useState({});

    useEffect(() => {
        if (show) {
            instance
                .get(`customers/company/plans`)
                .then((res) => {
                    setBackendPlans(res.data);
                    const initialConfigs = {};
                    if (Array.isArray(res.data)) {
                        res.data.forEach(plan => {
                            initialConfigs[plan.id] = { extraUsers: 0, extraAdmins: 0 };
                        });
                    }
                    setPlanConfigs(initialConfigs);
                })
                .catch((err) => console.error("Error fetching plans:", err));
        }
    }, [show]);

    const prepareSelectedPlanData = (planId) => {
        const selectedFrontendPlan = plans.find(p => p.id === planId);
        const selectedBackendPlan = getBackendPlan(planId);
        const config = planConfigs[planId] || { extraUsers: 0, extraAdmins: 0 };
        const totalPrice = getCalculatedPrice(selectedFrontendPlan);
        return {
            frontendPlan: selectedFrontendPlan,
            backendPlan: selectedBackendPlan,
            config: config,
            billingPeriod: billingPeriod,
            totalPrice: totalPrice,
            totalPriceNumeric: Number(totalPrice.replace(/[^0-9]/g, '')),
        };
    };

    const handleSelect = (planId) => {
        setSelectedPlan(planId);

        const data = prepareSelectedPlanData(planId);

        if (onPlanSelected) {
            onPlanSelected(data);
        }
    };

    const toggleExpand = (id) => {
        setExpandedPlans((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const getBackendPlan = (frontendId) => {
        if (!backendPlans.length) return null;
        const map = {
            'basic': 10,
            'pro': 20,
            'enterprise': 30
        };
        const type = map[frontendId];
        return backendPlans.find(p => p.plan_type === type);
    };

    const handleIncrement = (planId, type) => {
        setPlanConfigs(prev => {
            const current = prev[planId] || { extraUsers: 0, extraAdmins: 0 };
            const step = type === "extraUsers" ? 10 : 1;
            const value = current[type] || 0;

            return {
                ...prev,
                [planId]: { ...current, [type]: value + step }
            };
        });
    };

    const handleDecrement = (planId, type) => {
        setPlanConfigs(prev => {
            const current = prev[planId] || { extraUsers: 0, extraAdmins: 0 };
            const step = type === "extraUsers" ? 10 : 1;
            const value = current[type];

            if (value <= 0) return prev;

            return {
                ...prev,
                [planId]: { ...current, [type]: Math.max(0, value - step) }
            };
        });
    };

    const applyBillingPeriod = (price, billingPeriod) => {
        if (billingPeriod === "yearly") {
            return price * 0.75;
        }
        return price;
    };

    const getBasePrice = (plan) => {
        const backendPlan = getBackendPlan(plan.id);
        if (!backendPlan) {
            const staticPrice = Number(plan.price.replace(/[^0-9]/g, ''));
            return "$" + applyBillingPeriod(staticPrice, billingPeriod).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
        }
        const basePrice = Number(backendPlan.price_extra_clients);
        const adjustedPrice = applyBillingPeriod(basePrice, billingPeriod);
        return "$" + adjustedPrice.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    };

    const getCalculatedPrice = (plan) => {
        const backendPlan = getBackendPlan(plan.id);

        if (!backendPlan) {
            const staticPrice = Number(plan.price.replace(/[^0-9]/g, ""));
            return "$" + staticPrice.toLocaleString("es-CO");
        }

        const basePrice = Number(backendPlan.base_price);
        const priceExtraUsers = Number(backendPlan.price_extra_clients);
        const priceExtraAdmins = Number(backendPlan.price_extra_admins);

        const includedUsers = Number(backendPlan.clients_limit) || 50;
        const includedAdmins = Number(backendPlan.role_user_limit) || 4;

        const selectedConfig = planConfigs[plan.id] || { extraUsers: 0, extraAdmins: 0 };

        const extraUsers = selectedConfig.extraUsers || 0;
        const extraAdmins = selectedConfig.extraAdmins || 0;

        const multiplier = billingPeriod === "yearly" ? 12 : 1;

        const baseTotal = basePrice * multiplier;

        const extraUsersTotal = extraUsers * priceExtraUsers * multiplier;

        let subtotal = baseTotal + extraUsersTotal;

        if (billingPeriod === "yearly") {
            subtotal = subtotal * 0.8;
        }

        const extraAdminsTotal = extraAdmins * priceExtraAdmins * multiplier;

        const finalTotal = subtotal + extraAdminsTotal;

        return "$" + finalTotal.toLocaleString("es-CO", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
    };

    const isSelected = (planId) => selectedPlan === planId;

    return (
        <Modal show={show} onHide={onHide} size="xl" centered className="plans-modal">
            <Modal.Body className="d-flex flex-column justify-content-center align-items-center gap-4 p-4 py-5">

                <div className="plans-modal-header d-flex align-items-center justify-content-center w-100 position-relative py-3">
                    {source !== "edit" ? (
                        <button
                            className="btn p-0 border-0 bg-transparent position-absolute top-50 start-0 translate-middle-y ms-3"
                            onClick={onBack}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36" fill="none">
                                <path d="M25.5811 34.4609H9.88379C4.97749 34.4609 1 30.4835 1 25.5771V9.87988C1 4.97358 4.97749 0.996094 9.88379 0.996094H25.5811C30.4874 0.996094 34.4648 4.97358 34.4648 9.87988V25.5771L34.4531 26.0342C34.223 30.5768 30.5807 34.2191 26.0381 34.4492L25.5811 34.4609Z" stroke="#9DACBD" stroke-width="2" />
                                <path d="M10.9998 17.6331C10.9998 17.0369 11.2273 16.4405 11.6823 15.9855L20.7119 6.95586C21.6218 6.04596 23.0971 6.04596 24.007 6.95586C24.9169 7.86576 24.9169 9.34112 24.007 10.251L16.6249 17.6331L24.007 25.0152C24.9169 25.9251 24.9169 27.4005 24.007 28.3104C23.0971 29.2203 21.6218 29.2203 20.7119 28.3104L11.6823 19.2808C11.2273 18.8258 10.9998 18.2294 10.9998 17.6331Z" fill="#9DACBD" />
                            </svg>
                        </button>
                    ) : (
                        <button
                            className="btn p-0 border-0 bg-transparent position-absolute top-50 end-0 translate-middle-y me-3"
                            onClick={onHide}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="29" height="29" viewBox="0 0 29 29" fill="none">
                                <path d="M14.0605 1.5C20.9574 1.50007 26.6211 7.26551 26.6211 14.4678C26.6209 21.6698 20.9572 27.4345 14.0605 27.4346C7.1638 27.4346 1.50022 21.6699 1.5 14.4678C1.5 7.26547 7.16367 1.5 14.0605 1.5Z" stroke="#9DACBD" stroke-width="3" />
                                <path d="M8.65234 9.10938L18.9273 19.2901" stroke="#9DACBD" stroke-width="2" stroke-linecap="round" />
                                <path d="M18.9272 9.10938L8.65232 19.2901" stroke="#9DACBD" stroke-width="2" stroke-linecap="round" />
                            </svg>
                        </button>
                    )}
                    <div className="plans-modal-title">
                        Elige tu plan para tu prueba gratuita
                    </div>
                </div>

                <div className="d-flex flex-column align-items-center justify-content-center gap-2">
                    <span className="plans-modal-subtitle">
                        Selecciona el plan que mejor se adapte a los objetivos de tu centro deportivo.
                    </span>
                    <span className="plans-modal-subtitle">
                        Puedes cambiarlo cuando quieras.
                    </span>
                </div>

                <div className="billing-toggle d-flex align-items-center justify-content-between px-4 py-2">
                    <span className={`billing-label mensual ${billingPeriod === "monthly" ? "active" : ""}`}>
                        Mensual
                    </span>

                    <label className="toggle">
                        <input
                            type="checkbox"
                            className="toggle-input"
                            checked={billingPeriod === "yearly"}
                            onChange={() => setBillingPeriod(prev => prev === "monthly" ? "yearly" : "monthly")}
                        />
                        <span className="toggle-slider"></span>
                    </label>

                    <span className={`billing-label anual ${billingPeriod === "yearly" ? "active" : ""}`}>
                        Anual
                    </span>
                </div>

                <Row className="g-4">
                    {plans.map((plan) => (
                        <Col
                            key={plan.id}
                            xs={12}
                            md={4}
                            lg={4}
                        >
                            <Card
                                className={`${plan.highlighted ? "plan-card-highlight" : "plan-card"}`}
                            >
                                {plan.highlighted ? (
                                    <div className={`plan-chip d-flex flex-row align-items-center gap-2`}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M10.3072 7.22133C10.9493 5.62064 11.2704 4.8203 11.7919 4.70937C11.9291 4.68021 12.0708 4.68021 12.208 4.70937C12.7295 4.8203 13.0506 5.62064 13.6927 7.22133C14.0578 8.13161 14.2404 8.58675 14.582 8.89632C14.6778 8.98315 14.7818 9.06048 14.8926 9.12723C15.2874 9.3652 15.7803 9.40935 16.7661 9.49763C18.4348 9.64708 19.2692 9.72181 19.524 10.1976C19.5768 10.2961 19.6127 10.4028 19.6302 10.5132C19.7146 11.0462 19.1012 11.6042 17.8744 12.7204L17.5338 13.0303C16.9602 13.5521 16.6735 13.813 16.5076 14.1386C16.4081 14.3339 16.3414 14.5443 16.3101 14.7612C16.258 15.1229 16.342 15.5014 16.5099 16.2584L16.5699 16.5289C16.8711 17.8864 17.0217 18.5652 16.8337 18.8988C16.6649 19.1985 16.3538 19.3904 16.0102 19.4068C15.6277 19.425 15.0887 18.9858 14.0107 18.1074C13.3005 17.5287 12.9454 17.2394 12.5512 17.1263C12.191 17.023 11.8089 17.023 11.4487 17.1263C11.0545 17.2394 10.6994 17.5287 9.98917 18.1074C8.91119 18.9858 8.37221 19.425 7.98968 19.4068C7.64609 19.3904 7.33504 19.1985 7.16617 18.8988C6.97818 18.5652 7.12878 17.8864 7.42997 16.5289L7.48998 16.2584C7.65794 15.5014 7.74191 15.1229 7.6898 14.7612C7.65854 14.5443 7.59182 14.3339 7.49232 14.1386C7.32645 13.813 7.03968 13.5521 6.46613 13.0303L6.12546 12.7204C4.89867 11.6042 4.28527 11.0462 4.36975 10.5132C4.38724 10.4028 4.42312 10.2961 4.47589 10.1975C4.73069 9.72181 5.56507 9.64708 7.23384 9.49763C8.21962 9.40935 8.71251 9.3652 9.10735 9.12723C9.2181 9.06048 9.32211 8.98315 9.41793 8.89632C9.75954 8.58675 9.94211 8.13161 10.3072 7.22133Z" fill="white" stroke="white" stroke-width="2" />
                                        </svg> MÁS POPULAR
                                    </div>
                                ) : plan.disabled ? (
                                    <div className={`plan-chip plan-chip-disabled d-flex flex-row align-items-center gap-2`}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M12.0186 2.91406L12.1836 2.96387C12.5449 3.10184 12.74 3.39015 12.8301 3.5918C12.9229 3.79962 12.9544 4.01359 12.9697 4.15625C13.0014 4.45121 13 4.85227 13 5.27051V9.2002H13.8701C14.5411 9.2002 15.1589 9.19739 15.6387 9.26855C16.1352 9.34227 16.7407 9.52944 17.1201 10.123C17.4996 10.7169 17.4152 11.3455 17.2734 11.8271C17.1365 12.2923 16.8746 12.8513 16.5928 13.46L13.8613 19.3604C13.6856 19.74 13.5179 20.1041 13.3652 20.3584C13.2913 20.4815 13.1736 20.6629 13.002 20.8125C12.8117 20.9783 12.4496 21.1889 11.9814 21.0859C11.513 20.9828 11.2729 20.6386 11.1699 20.4082C11.0771 20.2004 11.0456 19.9864 11.0303 19.8438C10.9986 19.5488 11 19.1477 11 18.7295V14.7998H10.1299C9.45893 14.7998 8.84111 14.8026 8.36133 14.7314C7.86476 14.6577 7.25928 14.4706 6.87988 13.877C6.50039 13.2831 6.58475 12.6545 6.72656 12.1729C6.86352 11.7077 7.12542 11.1487 7.40723 10.54L10.1387 4.63965C10.3144 4.25999 10.4821 3.89594 10.6348 3.6416C10.7087 3.51853 10.8264 3.33713 10.998 3.1875C11.1646 3.04239 11.4629 2.86306 11.8486 2.88965L12.0186 2.91406Z" stroke="white" stroke-width="2" />
                                        </svg> PRÓXIMAMENTE
                                    </div>
                                ) : null}

                                <Card.Body className="d-flex flex-column justify-content-start align-items-start gap-3 p-0 pt-4">
                                    <div className="plan-card-title px-4">{plan.name}</div>
                                    <div className="plan-card-description px-4">{plan.description}</div>
                                    <div className={`d-flex flex-row justify-content-start align-items-end gap-2 px-4 ${plan.disabled && "plan-card-disabled"}`}>
                                        <div className="plan-card-price">{getBasePrice(plan)}</div>
                                        <div className="plan-card-price-note">COP / usuario / {billingPeriod === 'yearly' ? 'año' : 'mes'}</div>
                                    </div>
                                    <div className="w-100 d-flex flex-column align-items-center gap-3 px-4">
                                        <div className={`plan-config-box ${plan.disabled && "plan-config-box-disabled"} d-flex flex-column gap-3`}>
                                            <div className="plan-config-title">CONFIGURA TU GIMNASIO</div>
                                            <div className="plan-config-item">
                                                <div className="plan-config-label d-flex justify-content-between align-items-center">
                                                    <span className="plan-config-label-text">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                            <path d="M11.0126 16.5002C11.1557 13.6757 12.5251 11 15.5 11C18.4749 11 19.8443 13.6757 19.9874 16.5002C20.0014 16.7759 19.7761 17 19.5 17H11.5C11.2239 17 10.9986 16.7759 11.0126 16.5002Z" fill="#2A4157" fill-opacity="0.24" />
                                                            <path d="M4.0126 16.5002C4.15567 13.6757 5.52513 11 8.5 11C11.4749 11 12.8443 13.6757 12.9874 16.5002C13.0014 16.7759 12.7761 17 12.5 17H4.5C4.22386 17 3.99863 16.7759 4.0126 16.5002Z" fill="#2A4157" fill-opacity="0.24" />
                                                            <circle cx="15.5" cy="7.5" r="2.5" fill="#2A4157" fill-opacity="0.24" />
                                                            <circle cx="2.5" cy="2.5" r="2.5" transform="matrix(-1 0 0 1 11 5)" fill="#2A4157" fill-opacity="0.24" />
                                                            <path d="M7.01399 17.5002C7.17295 14.6757 8.69457 12 12 12C15.3054 12 16.827 14.6757 16.986 17.5002C17.0015 17.7759 16.7761 18 16.5 18H7.5C7.22386 18 6.99848 17.7759 7.01399 17.5002Z" fill="#222222" />
                                                            <circle cx="12" cy="8" r="3" fill="#222222" />
                                                        </svg>
                                                        Cantidad de Atletas
                                                    </span>
                                                    <span className="plan-config-label-right">
                                                        {(getBackendPlan(plan.id)?.clients_limit || 50) + (planConfigs[plan.id]?.extraUsers || 0)} usuarios
                                                    </span>
                                                </div>

                                                <div className="plan-config-counter d-flex flex-row justify-content-between align-items-center gap-2">
                                                    <button className="plan-counter-btn" onClick={() => handleDecrement(plan.id, 'extraUsers')}>-</button>
                                                    <div className="plan-counter-value">{planConfigs[plan.id]?.extraUsers || 0}</div>
                                                    <button className="plan-counter-btn" onClick={() => handleIncrement(plan.id, 'extraUsers')}>+</button>
                                                </div>

                                                <div className="plan-config-hint">Mínimo 50 usuarios para este plan</div>
                                            </div>
                                            <div className="plan-config-item">
                                                <div className="plan-config-label d-flex justify-content-between align-items-center">
                                                    <span className="plan-config-label-text">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                            <span className="plan-config-label-right">+0 extras</span>
                                                            <path d="M4.0126 16.5002C4.15567 13.6757 5.52513 11 8.5 11C11.4749 11 12.8443 13.6757 12.9874 16.5002C13.0014 16.7759 12.7761 17 12.5 17H4.5C4.22386 17 3.99863 16.7759 4.0126 16.5002Z" fill="#2A4157" fill-opacity="0.24" />
                                                            <circle cx="15.5" cy="7.5" r="2.5" fill="#2A4157" fill-opacity="0.24" />
                                                            <circle cx="2.5" cy="2.5" r="2.5" transform="matrix(-1 0 0 1 11 5)" fill="#2A4157" fill-opacity="0.24" />
                                                            <path d="M7.01399 17.5002C7.17295 14.6757 8.69457 12 12 12C15.3054 12 16.827 14.6757 16.986 17.5002C17.0015 17.7759 16.7761 18 16.5 18H7.5C7.22386 18 6.99848 17.7759 7.01399 17.5002Z" fill="#222222" />
                                                            <circle cx="12" cy="8" r="3" fill="#222222" />
                                                        </svg>
                                                        Admins Extra
                                                    </span>
                                                    <span className="plan-config-label-right">+{planConfigs[plan.id]?.extraAdmins || 0} extras</span>
                                                </div>

                                                <div className="plan-config-counter d-flex flex-row justify-content-between align-items-center">
                                                    <button className="plan-counter-btn" onClick={() => handleDecrement(plan.id, 'extraAdmins')}>−</button>
                                                    <div className="plan-counter-value">{planConfigs[plan.id]?.extraAdmins || 0}</div>
                                                    <button className="plan-counter-btn" onClick={() => handleIncrement(plan.id, 'extraAdmins')}>+</button>
                                                </div>

                                                <div className="plan-config-hint">Incluye {getBackendPlan(plan.id)?.role_user_limit || 4} administrativos, usuario adicional por ${Number(getBackendPlan(plan.id)?.price_extra_admins || 0).toLocaleString("es-CO")}</div>
                                            </div>
                                        </div>
                                        <div className={`plan-config-total ${plan.disabled && "plan-config-total-disabled"} d-flex flex-column gap-2`}>
                                            <div>
                                                <span className="plan-config-total-value"> {getCalculatedPrice(plan)}</span>
                                            </div>
                                            <div className="d-flex flex-row justify-content-between align-items-center w-100">
                                                <span className="plan-config-total-label"> Total estimado:</span>

                                                <div className="plan-config-total-note text-end">/{billingPeriod === 'yearly' ? 'año' : 'mes'}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        className={`plan-card-button mx-4 ${isSelected(plan.id) ? "plan-selected" : ""} ${plan.highlighted ? "plan-card-button-highlight" : "plan-card-button-normal"} ${plan.disabled && "plan-card-button-disabled"}`}
                                        onClick={() => handleSelect(plan.id)}
                                    >
                                        {isSelected(plan.id) ? "Seleccionado" : `Elegir ${plan.name}`}
                                    </button>

                                    <div className="plan-card-features p-4 w-100">
                                        <h5 className="plan-card-features-title mb-3">{plan.featuresTitle}:</h5>
                                        <div
                                            className={`plan-features-wrapper ${expandedPlans[plan.id] ? "expanded" : ""} d-flex flex-column align-items-start justify-content-center gap-3`}
                                        >
                                            {(expandedPlans[plan.id] ? plan.features : plan.features.slice(0, 5)).map((f, idx) => (
                                                <div
                                                    key={idx}
                                                    className="d-flex flex-row align-items-center gap-1 plan-card-features-label"
                                                >
                                                    {plan.disabled ? (<>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                                                            <path d="M6.03656 4.23397C6.4126 3.29651 6.60062 2.82778 6.90609 2.76281C6.98641 2.74573 7.06942 2.74573 7.14974 2.76281C7.45521 2.82778 7.64323 3.29651 8.01927 4.23397C8.23312 4.76709 8.34004 5.03365 8.54011 5.21495C8.59623 5.2658 8.65714 5.31109 8.72201 5.35018C8.95325 5.48956 9.24192 5.51541 9.81925 5.56711C10.7966 5.65464 11.2853 5.69841 11.4345 5.97703C11.4654 6.03474 11.4864 6.09722 11.4966 6.16187C11.5461 6.47404 11.1869 6.80088 10.4684 7.45455L10.2689 7.63607C9.93297 7.94167 9.76501 8.09447 9.66787 8.28517C9.60959 8.39956 9.57052 8.52275 9.55221 8.64982C9.52169 8.86164 9.57087 9.08331 9.66924 9.52665L9.70438 9.68505C9.88078 10.4801 9.96898 10.8776 9.85888 11.073C9.75998 11.2486 9.57782 11.3609 9.37658 11.3705C9.15255 11.3812 8.83689 11.124 8.20556 10.6096C7.78962 10.2706 7.58164 10.1012 7.35077 10.035C7.13979 9.97447 6.91605 9.97447 6.70506 10.035C6.47419 10.1012 6.26622 10.2706 5.85028 10.6096C5.21895 11.124 4.90328 11.3812 4.67925 11.3705C4.47802 11.3609 4.29585 11.2486 4.19695 11.073C4.08685 10.8776 4.17505 10.4801 4.35145 9.68505L4.38659 9.52665C4.48496 9.08331 4.53414 8.86164 4.50362 8.64982C4.48531 8.52275 4.44624 8.39956 4.38796 8.28517C4.29082 8.09447 4.12287 7.94167 3.78696 7.63607L3.58744 7.45455C2.86896 6.80088 2.50972 6.47404 2.55919 6.16187C2.56944 6.09722 2.59045 6.03474 2.62136 5.97703C2.77058 5.69841 3.25925 5.65464 4.23658 5.56711C4.81392 5.51541 5.10259 5.48956 5.33383 5.35018C5.39869 5.31109 5.45961 5.2658 5.51572 5.21495C5.71579 5.03365 5.82272 4.76709 6.03656 4.23397Z" fill="#9DACBD" stroke="#9DACBD" stroke-width="1.17133" />
                                                        </svg>
                                                    </>)
                                                        :
                                                        (<>
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                                <path d="M5.70924 0C4.58006 0 3.47624 0.334841 2.53736 0.96218C1.59848 1.58952 0.866711 2.48118 0.434592 3.52441C0.00247373 4.56763 -0.110588 5.71557 0.109704 6.82305C0.329996 7.93053 0.873748 8.94782 1.6722 9.74627C2.47065 10.5447 3.48794 11.0885 4.59542 11.3088C5.7029 11.5291 6.85084 11.416 7.89407 10.9839C8.93729 10.5518 9.82895 9.81999 10.4563 8.88112C11.0836 7.94224 11.4185 6.83841 11.4185 5.70923C11.4185 4.19505 10.817 2.74289 9.74628 1.6722C8.67559 0.601507 7.22342 0 5.70924 0ZM8.39258 4.09796L5.57602 8.00562C5.49213 8.11888 5.36969 8.19753 5.2318 8.22674C5.09391 8.25596 4.9501 8.23371 4.82748 8.16421L3.20986 7.21901C3.07863 7.13994 2.98419 7.01197 2.94731 6.86326C2.91043 6.71456 2.93414 6.55729 3.01321 6.42606C3.09228 6.29483 3.22025 6.20039 3.36896 6.16351C3.51766 6.12663 3.67493 6.15034 3.80616 6.22941L4.95435 6.91452L7.49179 3.43188C7.58596 3.33754 7.71018 3.27912 7.84291 3.26676C7.97563 3.25439 8.1085 3.28886 8.21848 3.36419C8.32846 3.43952 8.40861 3.55096 8.44504 3.67918C8.48148 3.80741 8.47189 3.94434 8.41795 4.06624L8.39258 4.09796Z" fill="#6FD195" />
                                                            </svg>
                                                        </>)
                                                    }
                                                    {f}
                                                </div>
                                            ))}
                                            {plan.features.length > 5 && (
                                                <button
                                                    className="btn btn-link text-center align-self-center plan-card-features-show-more"
                                                    onClick={() => toggleExpand(plan.id)}
                                                >
                                                    {expandedPlans[plan.id] ?
                                                        <>
                                                            Ocultar características
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                                <path d="M4 10L8 6L12 10" stroke="#3177BE" stroke-width="2" />
                                                            </svg>
                                                        </>
                                                        :
                                                        <>
                                                            Ver las {plan.features.length} características
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                                <path d="M12 6L8 10L4 6" stroke="#3177BE" stroke-width="2" />
                                                            </svg>
                                                        </>
                                                    }
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
                <div className="plans-modal-footer d-flex flex-column gap-4">
                    <span className="w-50 align-self-center">
                        Todos los planes incluyen una prueba gratuita de 30 días. Puedes cambiar a un plan superior, inferior o cancelarlo en cualquier momento.
                    </span>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default PlansModal;
